/**
 * RemoteComponents.jsx  ·  Mirror sync edition (fully updated)
 *
 * Changes in this version vs original:
 *
 *  MirrorView
 *    • Drag-to-scroll via Pointer Events API (mouse + touch)
 *    • Tap-vs-drag guard on word clicks (4px threshold)
 *    • iOS tap-to-play overlay when autoplay is blocked
 *    • .tp-mirror-video class for eager srcObject on Android
 *    • needsGesture state resets on stream change
 *
 *  RemotePanel  — Controls | Mirror | Cues tabs
 *
 *  WebRTCModal  (host path)
 *    • Video transceiver added upfront in offer
 *    • replaceVideoTrack() passed back via onRemoteConnected(dc, replaceVideoTrack)
 *
 *  WebRTCModal / StandaloneRemote  (remote path)
 *    • pc.ontrack eagerly sets srcObject on .tp-mirror-video + updates state
 *
 *  STATE messages include full words array over WebRTC
 *  JUMP_TO command sent by MirrorView tap-to-jump
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import QRCode from "react-qr-code";

import { useVoiceScroll } from "./hooks";
import { QUICK_CUES }     from "./constants";
import {
  CopyButton, VUMeter, MicIcon, Ico,
  RemoteIcon, TVIcon, ScanIcon, SignalIcon,
  AlertIcon, CheckCircleIcon, ZapIcon, CloseIcon,
  CueLabelIcon,
} from "./Overlays";
import { JsQRScanner } from "./JsQRScanner";

import {
  encodeForQR, decodeFromQR,
  extractCandidateFromSDP, extractFingerprintFromSDP,
  extractMidsFromSDP, extractMlinesFromSDP,
  reconstructAnswerSDP,
} from "./sdpCompact";

import { encodeAnswer, decodeAnswer } from "./StudioProvider";
import { createPeerConnection, waitForICEGathering } from "./iceConfig";
import "./remote.css";

// ── Design tokens ─────────────────────────────────────────────
const FONT_MONO  = "ui-monospace,'SF Mono','Cascadia Code',Menlo,Consolas,'Liberation Mono',monospace";
const FONT_SERIF = "ui-serif,Georgia,'Times New Roman',serif";
const FONT_SANS  = "-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,'Helvetica Neue',Arial,sans-serif";

const G       = "#ffffff";
const G_ALPHA = (a) => `rgba(255,255,255,${a})`;
const REC     = "#e05555";
const OK      = "#66bb6a";
const BG      = "#0c0c0c";
const SURFACE = "#141414";
const BORDER  = "#2a2a2a";
const BORDER2 = "#333";
const MUTED   = "#555";
const TEXT    = "#f0ece0";
const CTRL_BG = "#1a1a1a";

const T_BASE   = "background 140ms ease, border-color 140ms ease, color 140ms ease";
const T_SPRING = "transform 120ms cubic-bezier(.34,1.4,.64,1)";
const T_ALL    = `${T_BASE}, ${T_SPRING}`;

function rcBtnStyle(color, bg, border, size) {
  return {
    width: size, height: size, borderRadius: 8,
    background: bg, border: `1px solid ${border}`,
    color, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
    transition: `${T_BASE}, ${T_SPRING}`,
    WebkitTapHighlightColor: "transparent",
  };
}

function springProps() {
  return {
    onMouseDown:  e => { e.currentTarget.style.transform = "scale(.9)"; },
    onMouseUp:    e => { e.currentTarget.style.transform = "scale(1)";  },
    onTouchStart: e => { e.currentTarget.style.transform = "scale(.9)"; },
    onTouchEnd:   e => { e.currentTarget.style.transform = "scale(1)";  },
  };
}

// ── SDP helpers ───────────────────────────────────────────────
function extractOfferFromInput(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    const offerParam = url.searchParams.get("offer");
    if (offerParam) return decodeURIComponent(offerParam);
  } catch {}
  return trimmed;
}

function decodeOfferSDP(raw) {
  if (!raw) throw new Error("Empty offer code.");
  if (raw.startsWith("o:")) {
    const sdp = decodeFromQR(raw.slice(2));
    if (sdp) return sdp;
    throw new Error("Could not decode offer QR data.");
  }
  if (raw.startsWith("v=")) return raw;
  const sdp = decodeFromQR(raw);
  if (sdp && sdp.startsWith("v=")) return sdp;
  throw new Error("Invalid offer code — cannot decode SDP.");
}

// ── Cue presets ───────────────────────────────────────────────
const ACCENT_PRESETS = [
  "#ffffff","#ff4444","#4fc3f7","#ffb74d","#ff7043","#81c784",
  "#ef5350","#ffd54f","#66bb6a","#ce93d8","#a5d6a7","#f48fb1",
  "#80deea","#ffcc02","#d4af37",
];
const BG_PRESETS = [
  "#0c0c0c","#1a1a2e","#2a0a0a","#0a1a2a","#0a1a0a",
  "#100a1a","#1a1500","#2a1000","#0a0a0a","#141414",
];


// ─────────────────────────────────────────────────────────────
//  CueTab
// ─────────────────────────────────────────────────────────────
function CueTab({ sendCue }) {
  const [subTab, setSubTab] = useState("presets");
  const [label,  setLabel]  = useState("");
  const [accent, setAccent] = useState(G);
  const [bg,     setBg]     = useState(BG);
  const [icon,   setIcon]   = useState("standby");
  const [sent,   setSent]   = useState(false);

  const CUE_ICONS = ["standby","onair","timer","mic","cut","hold","clear","quiet","thumbsup","again"];

  const handleSend = () => {
    if (!label.trim()) return;
    sendCue({ icon, label: label.trim(), bg, accent });
    setSent(true);
    setTimeout(() => setSent(false), 1800);
  };

  const subTabStyle = (active) => ({
    flex: 1, padding: "6px 0",
    background: active ? G_ALPHA(.06) : "transparent",
    border: "none", borderBottom: `1px solid ${active ? G : BORDER}`,
    color: active ? G : MUTED,
    fontFamily: FONT_MONO, fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase",
    cursor: "pointer", transition: T_BASE,
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", borderBottom:`1px solid ${BORDER}` }}>
        <button style={subTabStyle(subTab==="presets")} onClick={() => setSubTab("presets")}>Presets</button>
        <button style={subTabStyle(subTab==="custom")}  onClick={() => setSubTab("custom")}>Custom</button>
      </div>

      {subTab === "presets" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {QUICK_CUES.map((cue, i) => (
            <button key={i} onClick={() => sendCue(cue)}
              style={{
                padding:"10px 8px",
                background:`${cue.bg}dd`,
                border:`1px solid ${cue.accent}55`,
                borderRadius:10, color:cue.accent,
                fontFamily:FONT_MONO, fontSize:10, fontWeight:600,
                cursor:"pointer", textAlign:"center",
                transition:`${T_BASE}, ${T_SPRING}`,
                WebkitTapHighlightColor:"transparent",
                display:"flex", flexDirection:"column", alignItems:"center", gap:5,
                letterSpacing:".04em",
              }}
              {...springProps()}>
              <span style={{ display:"flex", alignItems:"center", opacity:.85 }}>
                <CueLabelIcon name={cue.icon} size={15}/>
              </span>
              {cue.label}
            </button>
          ))}
        </div>
      )}

      {subTab === "custom" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{
            padding:"14px 16px",
            background:`${bg}ee`,
            border:`2px solid ${accent}66`,
            borderRadius:12,
            display:"flex", alignItems:"center", gap:12, minHeight:56,
          }}>
            <span style={{ color:accent, display:"flex" }}><CueLabelIcon name={icon} size={20}/></span>
            <span style={{ fontFamily:FONT_SERIF, fontSize:18, color:accent, flex:1, lineHeight:1.3 }}>
              {label || <span style={{ opacity:.35, fontFamily:FONT_MONO, fontSize:11 }}>Your cue label…</span>}
            </span>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <span style={{ fontSize:8, letterSpacing:".18em", textTransform:"uppercase", color:MUTED, fontFamily:FONT_MONO }}>Label</span>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => { if(e.key==="Enter") handleSend(); }}
              placeholder="e.g. Wrap it up"
              maxLength={40}
              style={{ background:BG, color:TEXT, border:`1px solid ${BORDER}`, borderRadius:8, padding:"9px 12px", fontFamily:FONT_MONO, fontSize:12, outline:"none", transition:"border-color 140ms ease, box-shadow 140ms ease" }}
              onFocus={e => { e.currentTarget.style.borderColor=G; e.currentTarget.style.boxShadow=`0 0 0 3px ${G_ALPHA(.07)}`; }}
              onBlur={e =>  { e.currentTarget.style.borderColor=BORDER; e.currentTarget.style.boxShadow="none"; }}
            />
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <span style={{ fontSize:8, letterSpacing:".18em", textTransform:"uppercase", color:MUTED, fontFamily:FONT_MONO }}>Icon</span>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {CUE_ICONS.map(ic => (
                <button key={ic} onClick={() => setIcon(ic)} title={ic}
                  style={{
                    width:32, height:32, borderRadius:7,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    background:icon===ic ? G_ALPHA(.1) : BG,
                    border:`1px solid ${icon===ic ? G : BORDER}`,
                    color:icon===ic ? G : MUTED,
                    cursor:"pointer", transition:T_BASE,
                  }}>
                  <CueLabelIcon name={ic} size={14}/>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <span style={{ fontSize:8, letterSpacing:".18em", textTransform:"uppercase", color:MUTED, fontFamily:FONT_MONO }}>Text Colour</span>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap", alignItems:"center" }}>
              {ACCENT_PRESETS.map(c => (
                <button key={c} onClick={() => setAccent(c)}
                  style={{ width:22, height:22, borderRadius:5, background:c, border:`2px solid ${accent===c ? "#fff" : "transparent"}`, cursor:"pointer", flexShrink:0, transition:T_SPRING }}
                  {...springProps()} />
              ))}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <span style={{ fontSize:8, letterSpacing:".18em", textTransform:"uppercase", color:MUTED, fontFamily:FONT_MONO }}>Background</span>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap", alignItems:"center" }}>
              {BG_PRESETS.map(c => (
                <button key={c} onClick={() => setBg(c)}
                  style={{ width:22, height:22, borderRadius:5, background:c, border:`2px solid ${bg===c ? "#fff" : G_ALPHA(.15)}`, cursor:"pointer", flexShrink:0, transition:T_SPRING }}
                  {...springProps()} />
              ))}
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={!label.trim()}
            style={{
              width:"100%", padding:"12px 0",
              background: sent ? "rgba(102,187,106,.2)" : label.trim() ? G : BORDER,
              border: sent ? "1px solid rgba(102,187,106,.5)" : "none",
              borderRadius:9,
              color: sent ? OK : label.trim() ? BG : MUTED,
              fontFamily:FONT_MONO, fontSize:12, fontWeight:700,
              letterSpacing:".08em", textTransform:"uppercase",
              cursor:label.trim() ? "pointer" : "not-allowed",
              transition:"all .2s cubic-bezier(.34,1.4,.64,1)",
              display:"flex", alignItems:"center", justifyContent:"center", gap:7,
            }}
            {...(label.trim() ? springProps() : {})}>
            {sent
              ? <><svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>Sent</>
              : "Send Cue"}
          </button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  MirrorView
//  Shows the full teleprompter word list synced to the host.
//
//  Props
//    words        string[]         — full script word array from STATE
//    current      number           — host's current word index
//    onJump       (i) => void      — called when user taps a word
//    videoStream  MediaStream|null — host's camera/screen track (WebRTC)
// ─────────────────────────────────────────────────────────────
function MirrorView({ words, current, onJump, videoStream }) {
  const containerRef = useRef(null);
  const videoRef     = useRef(null);
  const wordRefs     = useRef({});
  const dragRef      = useRef({ active: false, startY: 0, startScroll: 0, moved: false });

  const [hasVideo,     setHasVideo]     = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);

  // ── Pointer drag-to-scroll ───────────────────────────────
  const onPointerDown = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return;
    dragRef.current = { active: true, startY: e.clientY, startScroll: el.scrollTop, moved: false };
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dy = e.clientY - d.startY;
    if (Math.abs(dy) > 4) d.moved = true;
    if (d.moved && containerRef.current) {
      containerRef.current.scrollTop = d.startScroll - dy;
    }
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // ── Wire video stream to <video> element ─────────────────
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Reset gesture prompt whenever stream changes
    setNeedsGesture(false);

    if (videoStream && videoStream.active) {
      el.srcObject = videoStream;
      el.play().catch(() => {
        // iOS Safari blocks autoplay until a user gesture
        setNeedsGesture(true);
      });

      const onActive   = () => setHasVideo(true);
      const onInactive = () => setHasVideo(false);
      videoStream.addEventListener("active",   onActive);
      videoStream.addEventListener("inactive", onInactive);

      // Check immediately — stream may already be active
      setHasVideo(
        videoStream.active &&
        videoStream.getVideoTracks().some(t => t.readyState === "live")
      );

      return () => {
        videoStream.removeEventListener("active",   onActive);
        videoStream.removeEventListener("inactive", onInactive);
      };
    } else {
      el.srcObject = null;
      setHasVideo(false);
    }
  }, [videoStream]);

  // ── Auto-scroll to current word, centred ────────────────
  useEffect(() => {
    const el        = wordRefs.current[current];
    const container = containerRef.current;
    if (!el || !container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect        = el.getBoundingClientRect();
    const scrollTarget  =
      container.scrollTop +
      elRect.top - containerRect.top -
      containerRect.height / 2 +
      elRect.height / 2;
    container.scrollTo({ top: scrollTarget, behavior: "smooth" });
  }, [current]);

  if (!words.length) {
    return (
      <div style={{
        textAlign:"center", color:MUTED, fontFamily:FONT_MONO, fontSize:11,
        padding:"32px 16px", background:SURFACE, borderRadius:10,
        border:`1px solid ${BORDER}`,
      }}>
        Waiting for script…
        <div style={{ marginTop:8, fontSize:9, opacity:.5 }}>
          Play the teleprompter on the host device
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:"relative", borderRadius:10, border:`1px solid ${BORDER}`, overflow:"hidden", height:300 }}>

      {/* Background video — host camera or screen share */}
      <video
        ref={videoRef}
        className="tp-mirror-video"
        autoPlay
        muted
        playsInline
        style={{
          position:"absolute", inset:0,
          width:"100%", height:"100%",
          objectFit:"cover",
          opacity: hasVideo ? 1 : 0,
          transition:"opacity .4s ease",
        }}
        onLoadedMetadata={() => setHasVideo(true)}
        onEmptied={() => setHasVideo(false)}
      />

      {/* Dim overlay */}
      <div style={{
        position:"absolute", inset:0,
        background: hasVideo ? "rgba(12,12,12,.72)" : BG,
        transition:"background .4s ease",
      }} />

      {/* iOS tap-to-play overlay */}
      {needsGesture && (
        <div
          onClick={() => {
            videoRef.current?.play()
              .then(() => setNeedsGesture(false))
              .catch(() => {});
          }}
          style={{
            position:"absolute", inset:0, zIndex:10,
            display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center",
            background:"rgba(12,12,12,.75)",
            cursor:"pointer",
          }}
        >
          <div style={{
            width:52, height:52, borderRadius:"50%",
            background:"rgba(255,255,255,.15)",
            border:"1px solid rgba(255,255,255,.3)",
            display:"flex", alignItems:"center", justifyContent:"center",
            marginBottom:10,
          }}>
            <svg viewBox="0 0 24 24" width={22} height={22} fill="white">
              <polygon points="6,3 20,12 6,21"/>
            </svg>
          </div>
          <span style={{
            fontFamily:FONT_MONO, fontSize:10,
            color:"rgba(255,255,255,.6)",
            letterSpacing:".12em", textTransform:"uppercase",
          }}>
            Tap to see video
          </span>
        </div>
      )}

      {/* Subtle focus line at centre */}
      <div style={{
        position:"absolute", left:0, right:0, top:"50%",
        transform:"translateY(-50%)", height:1,
        background:"rgba(255,255,255,.07)", zIndex:2, pointerEvents:"none",
      }} />

      {/* Top / bottom fade masks */}
      <div style={{
        position:"absolute", top:0, left:0, right:0, height:60,
        background:"linear-gradient(to bottom, rgba(12,12,12,.9), transparent)",
        zIndex:2, pointerEvents:"none",
      }} />
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:60,
        background:"linear-gradient(to top, rgba(12,12,12,.9), transparent)",
        zIndex:2, pointerEvents:"none",
      }} />

      {/* Scrollable word list */}
      <div
        ref={containerRef}
        style={{
          position:"relative", zIndex:1,
          height:"100%", overflowY:"auto",
          scrollbarWidth:"none", msOverflowStyle:"none",
          cursor:"grab",
          // Allow native touch scroll on mobile — pointer events still fire
          touchAction:"pan-y",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <style>{`.tp-mirror-scroll::-webkit-scrollbar{display:none}`}</style>
        <div
          className="tp-mirror-scroll"
          style={{ padding:"150px 20px", lineHeight:1.9, textAlign:"left" }}
        >
          {words.map((w, i) => {
            const isCurrent = i === current;
            const isPast    = i < current;
            return (
              <span
                key={i}
                ref={el => { if (el) wordRefs.current[i] = el; else delete wordRefs.current[i]; }}
                onClick={() => { if (!dragRef.current.moved) onJump(i); }}
                title={`Jump to word ${i + 1}`}
                style={{
                  display: "inline",
                  fontFamily: FONT_SERIF,
                  fontSize: isCurrent ? 21 : 17,
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent ? "#ffffff"
                       : isPast    ? "rgba(255,255,255,.18)"
                       :             "rgba(255,255,255,.48)",
                  cursor: "pointer",
                  padding: "1px 2px",
                  borderRadius: 3,
                  background: isCurrent ? "rgba(255,255,255,.1)" : "transparent",
                  transition: "color .12s ease, font-size .1s ease, background .12s ease",
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                }}
              >
                {w}{" "}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  RemotePanel
//  Shared UI for BroadcastChannel and WebRTC remotes.
// ─────────────────────────────────────────────────────────────
export function RemotePanel({
  send, sendCue,
  rPlaying, setRPlaying,
  rSpeed, setRSpeed,
  rStatus,
  rWords = [],
  remoteStream = null,
  onJump,
}) {
  const [voiceScroll, setVoiceScroll] = useState(false);
  const [activeTab,   setActiveTab]   = useState("controls");

  const { error: vsError, level: vsLevel } = useVoiceScroll({
    enabled: voiceScroll,
    onAdvance: () => send("NEXT"),
    silenceThreshold: 0.018,
    silenceMs: 550,
  });

  const progress = rStatus.current < 0 ? 0 : ((rStatus.current + 1) / rStatus.total * 100);

  const handlePlayPause = () => {
    if (rPlaying) { setRPlaying(false); send("PAUSE"); }
    else          { setRPlaying(true);  send("PLAY"); }
  };

  const handleJump = useCallback((i) => {
    onJump?.(i);
  }, [onJump]);

  const tabStyle = (active) => ({
    flex: 1, padding: "8px 0",
    background: active ? G_ALPHA(.06) : "transparent",
    border: "none", borderBottom: `2px solid ${active ? G : "transparent"}`,
    color: active ? G : MUTED,
    fontFamily: FONT_MONO, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
    cursor: "pointer", transition: T_BASE,
  });

  const MiniTransport = () => (
    <div style={{
      display:"flex", gap:8, alignItems:"center", justifyContent:"center",
      paddingTop:10, borderTop:`1px solid ${BORDER}`,
    }}>
      <button onClick={() => send("STOP")} style={rcBtnStyle(REC, "rgba(42,14,14,.9)", "#5a2020", 40)} {...springProps()}>
        <Ico size={15}><rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" stroke="none"/></Ico>
      </button>
      <button onClick={() => send("PREV")} style={rcBtnStyle(TEXT, CTRL_BG, BORDER, 48)} {...springProps()}>
        <Ico size={18}><polygon points="19,20 9,12 19,4"/><line x1="5" y1="19" x2="5" y2="5"/></Ico>
      </button>
      <button
        onClick={handlePlayPause}
        style={{
          ...rcBtnStyle(rPlaying ? BG : G, rPlaying ? G : CTRL_BG, rPlaying ? G : BORDER, 64),
          borderRadius:"50%",
          boxShadow: rPlaying ? `0 0 0 3px ${G_ALPHA(.12)}` : "none",
          transition:`${T_ALL}, box-shadow 200ms ease`,
        }}
        {...springProps()}
      >
        {rPlaying
          ? <Ico size={22}><rect x="5" y="3" width="5" height="18" rx="1" fill="currentColor" stroke="none"/><rect x="14" y="3" width="5" height="18" rx="1" fill="currentColor" stroke="none"/></Ico>
          : <Ico size={22}><polygon points="6,3 20,12 6,21" fill="currentColor" stroke="none"/></Ico>}
      </button>
      <button onClick={() => send("NEXT")} style={rcBtnStyle(TEXT, CTRL_BG, BORDER, 48)} {...springProps()}>
        <Ico size={18}><polygon points="5,4 15,12 5,20"/><line x1="19" y1="5" x2="19" y2="19"/></Ico>
      </button>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, width:"100%" }}>

      {/* Tab bar */}
      <div style={{ display:"flex", borderBottom:`1px solid ${BORDER}`, marginBottom:2 }}>
        <button style={tabStyle(activeTab==="controls")} onClick={() => setActiveTab("controls")}>Controls</button>
        <button style={tabStyle(activeTab==="mirror")}   onClick={() => setActiveTab("mirror")}>Mirror</button>
        <button style={tabStyle(activeTab==="cues")}     onClick={() => setActiveTab("cues")}>Cues</button>
      </div>

      {/* ── Controls tab ── */}
      {activeTab === "controls" && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:10, letterSpacing:".12em", color:MUTED, textTransform:"uppercase", fontFamily:FONT_MONO }}>
              {rStatus.status}
            </span>
            <span style={{ fontSize:11, color:G, fontWeight:600, fontFamily:FONT_MONO }}>
              {Math.max(0, rStatus.current+1)} / {rStatus.total}
            </span>
          </div>

          <div style={{ height:3, background:BORDER, borderRadius:2, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${progress.toFixed(1)}%`, background:G, borderRadius:2, transition:"width .25s ease" }} />
          </div>

          <div style={{ display:"flex", gap:10, alignItems:"center", justifyContent:"center" }}>
            <button onClick={() => send("STOP")} style={rcBtnStyle(REC, "rgba(42,14,14,.9)", "#5a2020", 52)} {...springProps()}>
              <Ico size={18}><rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" stroke="none"/></Ico>
            </button>
            <button onClick={() => send("PREV")} style={rcBtnStyle(TEXT, CTRL_BG, BORDER, 64)} {...springProps()}>
              <Ico size={22}><polygon points="19,20 9,12 19,4"/><line x1="5" y1="19" x2="5" y2="5"/></Ico>
            </button>
            <button
              onClick={handlePlayPause}
              style={{
                ...rcBtnStyle(rPlaying ? BG : G, rPlaying ? G : CTRL_BG, rPlaying ? G : BORDER, 84),
                borderRadius:"50%",
                boxShadow: rPlaying ? `0 0 0 4px ${G_ALPHA(.12)}` : "none",
                transition:`${T_ALL}, box-shadow 200ms ease`,
              }}
              {...springProps()}
            >
              {rPlaying
                ? <Ico size={28}><rect x="5" y="3" width="5" height="18" rx="1" fill="currentColor" stroke="none"/><rect x="14" y="3" width="5" height="18" rx="1" fill="currentColor" stroke="none"/></Ico>
                : <Ico size={28}><polygon points="6,3 20,12 6,21" fill="currentColor" stroke="none"/></Ico>}
            </button>
            <button onClick={() => send("NEXT")} style={rcBtnStyle(TEXT, CTRL_BG, BORDER, 64)} {...springProps()}>
              <Ico size={22}><polygon points="5,4 15,12 5,20"/><line x1="19" y1="5" x2="19" y2="19"/></Ico>
            </button>
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:9, letterSpacing:".15em", color:MUTED, textTransform:"uppercase", fontFamily:FONT_MONO, flexShrink:0 }}>Speed</span>
            <input
              type="range" min="1" max="10" step="1"
              value={rSpeed} className="tp-range" style={{ flex:1 }}
              onChange={e => { const v=parseInt(e.target.value); setRSpeed(v); send("SET_SPEED", { value:v }); }}
            />
            <span style={{ fontSize:12, fontWeight:600, color:G, minWidth:16, fontFamily:FONT_MONO }}>{rSpeed}</span>
          </div>

          {/* Voice scroll */}
          <div style={{
            display:"flex", flexDirection:"column", gap:8,
            padding:"10px 12px",
            background: voiceScroll ? G_ALPHA(.04) : BG,
            border:`1px solid ${voiceScroll ? G_ALPHA(.25) : BORDER}`,
            borderRadius:9, transition:T_BASE,
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <MicIcon size={13}/>
                <span style={{ fontSize:10, letterSpacing:".1em", color: voiceScroll ? G : MUTED, textTransform:"uppercase", fontFamily:FONT_MONO, transition:T_BASE }}>
                  Voice Scroll
                </span>
              </div>
              <button
                onClick={() => setVoiceScroll(v => !v)}
                style={{
                  padding:"4px 12px", borderRadius:20,
                  background: voiceScroll ? G : CTRL_BG,
                  border:`1px solid ${voiceScroll ? G : BORDER}`,
                  color: voiceScroll ? BG : MUTED,
                  fontFamily:FONT_MONO, fontSize:9, letterSpacing:".1em", textTransform:"uppercase",
                  cursor:"pointer", fontWeight: voiceScroll ? 700 : 400, transition:T_ALL,
                }}
                {...springProps()}>
                {voiceScroll ? "ON" : "OFF"}
              </button>
            </div>
            {voiceScroll && (
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <VUMeter level={vsLevel} active={voiceScroll}/>
                <span style={{ fontSize:9, color:MUTED, fontFamily:FONT_MONO }}>Speak — pause — word advances</span>
              </div>
            )}
            {vsError && (
              <div style={{ fontSize:9, color:"#ef5350", display:"flex", alignItems:"center", gap:5, fontFamily:FONT_MONO }}>
                <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                {vsError}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Mirror tab ── */}
      {activeTab === "mirror" && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, height:2, background:BORDER, borderRadius:1, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${progress.toFixed(1)}%`, background:G, borderRadius:1, transition:"width .25s ease" }}/>
            </div>
            <span style={{ fontFamily:FONT_MONO, fontSize:10, color:MUTED, flexShrink:0 }}>
              {Math.max(0, rStatus.current+1)}/{rStatus.total}
            </span>
            {remoteStream && (
              <span style={{
                fontFamily:FONT_MONO, fontSize:8, color:OK,
                letterSpacing:".1em", textTransform:"uppercase",
                border:`1px solid ${OK}44`, borderRadius:10, padding:"2px 7px",
              }}>
                ▶ Live
              </span>
            )}
          </div>

          <MirrorView
            words={rWords}
            current={rStatus.current}
            onJump={handleJump}
            videoStream={remoteStream}
          />

          <MiniTransport/>

          <div style={{ fontSize:9, color:MUTED, fontFamily:FONT_MONO, textAlign:"center", letterSpacing:".06em" }}>
            Tap any word to jump • {remoteStream ? "Live video from host" : "Video appears when host enables Camera or Screen"}
          </div>
        </div>
      )}

      {/* ── Cues tab ── */}
      {activeTab === "cues" && <CueTab sendCue={sendCue}/>}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  RemoteController — BroadcastChannel same-browser remote
// ─────────────────────────────────────────────────────────────
export function RemoteController({ roomId }) {
  const [rPlaying, setRPlaying] = useState(false);
  const [rSpeed,   setRSpeed]   = useState(4);
  const [rStatus,  setRStatus]  = useState({ current:-1, total:0, status:"READY" });
  const [rWords,   setRWords]   = useState([]);
  const channelRef = useRef(null);

  useEffect(() => {
    const ch = new BroadcastChannel(`tp-${roomId}`);
    channelRef.current = ch;
    ch.onmessage = (e) => {
      if (e.data?.type !== "STATE") return;
      const p = e.data.payload;
      setRPlaying(p.playing);
      setRSpeed(p.speed);
      setRStatus({ current:p.current, total:p.words.length, status:p.status });
      setRWords(p.words);
    };
    ch.postMessage({ type:"CMD", cmd:"REQUEST_STATE" });
    return () => ch.close();
  }, [roomId]);

  const send = useCallback((cmd, payload={}) =>
    channelRef.current?.postMessage({ type:"CMD", cmd, ...payload }), []);

  const sendCue = useCallback((cue) =>
    channelRef.current?.postMessage({ type:"CMD", cmd:"CUE", label:cue.label, bg:cue.bg, accent:cue.accent }), []);

  const onJump = useCallback((i) =>
    channelRef.current?.postMessage({ type:"CMD", cmd:"JUMP_TO", index:i }), []);

  return (
    <div className="rc-root">
      <div className="rc-badge"><RemoteIcon size={11}/>Remote Controller</div>
      <RemotePanel
        send={send} sendCue={sendCue}
        rPlaying={rPlaying} setRPlaying={setRPlaying}
        rSpeed={rSpeed}     setRSpeed={setRSpeed}
        rStatus={rStatus}
        rWords={rWords}
        remoteStream={null}
        onJump={onJump}
      />
      <div className="rc-hint">Tap any word in Mirror tab to jump position</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  WebRTCRemotePanel — WebRTC data-channel remote
// ─────────────────────────────────────────────────────────────
export function WebRTCRemotePanel({ dc, remoteStream = null }) {
  const [rPlaying, setRPlaying] = useState(false);
  const [rSpeed,   setRSpeed]   = useState(4);
  const [rStatus,  setRStatus]  = useState({ current:-1, total:0, status:"READY" });
  const [rWords,   setRWords]   = useState([]);

  useEffect(() => {
    if (!dc) return;
    const handler = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "STATE") {
          setRPlaying(msg.playing);
          setRSpeed(msg.speed);
          setRStatus({
            current: msg.current,
            total: msg.words?.length ?? msg.words_count ?? 0,
            status: msg.status,
          });
          if (msg.words) setRWords(msg.words);
        }
      } catch {}
    };
    dc.addEventListener("message", handler);
    if (dc.readyState === "open") dc.send(JSON.stringify({ type:"CMD", cmd:"REQUEST_STATE" }));
    return () => dc.removeEventListener("message", handler);
  }, [dc]);

  const send = useCallback((cmd, extra={}) => {
    if (dc?.readyState === "open") dc.send(JSON.stringify({ type:"CMD", cmd, ...extra }));
  }, [dc]);

  const sendCue = useCallback((cue) => {
    if (dc?.readyState === "open")
      dc.send(JSON.stringify({ type:"CMD", cmd:"CUE", label:cue.label, bg:cue.bg, accent:cue.accent }));
  }, [dc]);

  const onJump = useCallback((i) => {
    if (dc?.readyState === "open")
      dc.send(JSON.stringify({ type:"CMD", cmd:"JUMP_TO", index:i }));
  }, [dc]);

  return (
    <RemotePanel
      send={send} sendCue={sendCue}
      rPlaying={rPlaying} setRPlaying={setRPlaying}
      rSpeed={rSpeed}     setRSpeed={setRSpeed}
      rStatus={rStatus}
      rWords={rWords}
      remoteStream={remoteStream}
      onJump={onJump}
    />
  );
}


// ─────────────────────────────────────────────────────────────
//  StandaloneRemote — full-page remote (?remote-setup)
// ─────────────────────────────────────────────────────────────
export function StandaloneRemote() {
  const [rtcDC,        setRtcDC]        = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [showScanner,  setShowScanner]  = useState(false);
  const [pasteInput,   setPasteInput]   = useState("");
  const [generating,   setGenerating]   = useState(false);
  const [answerCode,   setAnswerCode]   = useState("");
  const [answerUrl,    setAnswerUrl]    = useState("");
  const [errorMsg,     setErrorMsg]     = useState("");
  const [step,         setStep]         = useState("scan");
  const [disconnected, setDisconnected] = useState(false);
  const pcRef = useRef(null);

  const initialOffer = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get("offer");
      return raw ? decodeURIComponent(raw) : null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (initialOffer && step === "scan") processOffer(initialOffer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const processOffer = async (raw) => {
    const extracted = extractOfferFromInput(raw);
    if (!extracted) return;
    setGenerating(true); setErrorMsg(""); setShowScanner(false); setDisconnected(false);
    if (pcRef.current) { try { pcRef.current.close(); } catch {} }
    try {
      const sdp = decodeOfferSDP(extracted);
      const pc  = createPeerConnection();
      pcRef.current = pc;

      // Capture host's video track — set srcObject eagerly on any mounted
      // .tp-mirror-video element so Android doesn't miss the first frame
      pc.ontrack = (e) => {
        const stream = e.streams?.[0] || new MediaStream([e.track]);
        const videoEl = document.querySelector(".tp-mirror-video");
        if (videoEl) {
          videoEl.srcObject = stream;
          videoEl.play().catch(() => {});
        }
        setRemoteStream(stream);
      };

      pc.ondatachannel = (e) => {
        const dc = e.channel;
        const handleOpen = () => { setRtcDC(dc); setStep("connected"); };
        if (dc.readyState === "open") handleOpen();
        else dc.onopen = handleOpen;
        dc.onclose = () => {
          setRtcDC(null);
          setRemoteStream(null);
          setDisconnected(true);
          setStep("scan");
          setAnswerCode(""); setAnswerUrl("");
        };
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed") { try { pc.restartIce(); } catch {} }
      };

      await pc.setRemoteDescription(new RTCSessionDescription({ type:"offer", sdp }));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForICEGathering(pc);

      const localSDP  = pc.localDescription.sdp;
      const candidate = extractCandidateFromSDP(localSDP);
      const fpHex     = extractFingerprintFromSDP(localSDP);
      const ufrag     = localSDP.match(/a=ice-ufrag:(\S+)/)?.[1] ?? "";
      const pwd       = localSDP.match(/a=ice-pwd:(\S+)/)?.[1]   ?? "";

      if (!candidate) throw new Error("No ICE candidate found. Try a different network.");
      if (!ufrag || !pwd) throw new Error("Missing ICE credentials.");

      const encoded = encodeAnswer(candidate.ip, candidate.port, fpHex, ufrag, pwd);
      if (!encoded) throw new Error("Answer encoding failed.");

      const url = (() => {
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("offer"); u.searchParams.delete("remote-setup");
          u.searchParams.set("answer", encodeURIComponent(encoded));
          return u.toString();
        } catch { return ""; }
      })();

      setAnswerCode(encoded); setAnswerUrl(url);
      setStep("answer"); setGenerating(false);
    } catch (e) {
      setErrorMsg(e.message || "Setup failed"); setGenerating(false);
    }
  };

  // ── Style helpers ─────────────────────────────────────────
  const inputStyle = {
    flex:1, background:BG, color:TEXT,
    border:`1px solid ${BORDER}`, borderRadius:8,
    padding:"10px 12px", fontFamily:FONT_MONO, fontSize:13,
    outline:"none", transition:"border-color 140ms ease, box-shadow 140ms ease",
  };
  const primaryBtn = {
    width:"100%", padding:"12px 0", background:G, border:"none",
    borderRadius:8, color:BG, fontFamily:FONT_MONO, fontSize:12,
    fontWeight:600, letterSpacing:".08em", textTransform:"uppercase",
    cursor:"pointer", transition:T_ALL,
  };
  const ghostBtn = {
    width:"100%", padding:"10px 0", background:"transparent",
    border:`1px solid ${BORDER}`, borderRadius:8, color:MUTED,
    fontFamily:FONT_MONO, fontSize:10, letterSpacing:".1em",
    textTransform:"uppercase", cursor:"pointer", transition:T_ALL,
  };
  const card = {
    background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:14,
    padding:"24px 20px", display:"flex", flexDirection:"column", gap:16,
    width:"100%", maxWidth:360, boxSizing:"border-box",
    boxShadow:"0 8px 32px rgba(0,0,0,.5)",
  };
  const banner = {
    display:"flex", gap:10, alignItems:"flex-start",
    background:G_ALPHA(.04), border:`1px solid ${G_ALPHA(.15)}`,
    borderRadius:10, padding:"12px 14px",
    fontSize:11, fontFamily:FONT_MONO, color:G_ALPHA(.7),
    letterSpacing:".05em", lineHeight:1.7,
  };

  return (
    <div className="rc-root" style={{ justifyContent:"flex-start", paddingTop:36 }}>
      <div className="rc-badge"><RemoteIcon size={11}/>Teleprompter Remote</div>

      {disconnected && step === "scan" && (
        <div style={{ ...card, borderColor:"rgba(224,85,85,.3)", background:"rgba(42,14,14,.6)" }}>
          <div style={{ ...banner, borderColor:"rgba(224,85,85,.3)", background:"rgba(224,85,85,.06)", color:"rgba(239,83,80,.9)" }}>
            <span style={{ display:"flex" }}><ZapIcon size={15}/></span>
            <div>Connection lost. Scan the host QR again to reconnect.</div>
          </div>
          <button onClick={() => setDisconnected(false)} style={{ ...primaryBtn, background:REC, color:"#fff" }} {...springProps()}>
            Try Again
          </button>
        </div>
      )}

      {step === "scan" && !disconnected && (
        <div style={card}>
          <div style={banner}>
            <span style={{ display:"flex" }}><SignalIcon size={15}/></span>
            <div>
              On the <strong style={{ color:G }}>host device</strong>, open the teleprompter,
              click the QR button and choose <em>I'm the Host</em>. Then scan that QR here.
            </div>
          </div>
          <button
            onClick={() => setShowScanner(s => !s)}
            style={{
              ...primaryBtn,
              background: showScanner ? G_ALPHA(.08) : G,
              color: showScanner ? G : BG,
              border: showScanner ? `1px solid ${G_ALPHA(.35)}` : "none",
            }}
            {...springProps()}>
            {showScanner ? "Hide Camera" : "Scan Host QR"}
          </button>
          {showScanner && (
            <JsQRScanner
              label="Scan the host offer QR"
              onScan={(code) => processOffer(code)}
              onClose={() => setShowScanner(false)}
            />
          )}
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, height:1, background:BORDER }}/>
            <span style={{ fontSize:10, letterSpacing:".12em", textTransform:"uppercase", fontFamily:FONT_MONO, color:MUTED }}>or paste code</span>
            <div style={{ flex:1, height:1, background:BORDER }}/>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <input
              value={pasteInput}
              onChange={e => { setPasteInput(e.target.value); setErrorMsg(""); }}
              onKeyDown={e => { if(e.key==="Enter" && !generating) processOffer(pasteInput); }}
              placeholder="Paste offer code…"
              style={inputStyle}
              onFocus={e=>{ e.currentTarget.style.borderColor=G; e.currentTarget.style.boxShadow=`0 0 0 3px ${G_ALPHA(.07)}`; }}
              onBlur={e=> { e.currentTarget.style.borderColor=BORDER; e.currentTarget.style.boxShadow="none"; }}
            />
            <button
              onClick={() => processOffer(pasteInput)}
              disabled={generating}
              style={{ padding:"0 18px", background:G, border:"none", borderRadius:8, color:BG, fontFamily:FONT_MONO, fontSize:11, fontWeight:600, cursor:"pointer", transition:T_ALL }}
              {...springProps()}>
              {generating ? "…" : "Go"}
            </button>
          </div>
          {errorMsg && (
            <div style={{ fontSize:10, color:"#ef5350", padding:"6px 10px", background:"rgba(224,85,85,.08)", borderRadius:6, border:"1px solid rgba(224,85,85,.2)", fontFamily:FONT_MONO }}>
              {errorMsg}
            </div>
          )}
        </div>
      )}

      {step === "answer" && (
        <div style={{ ...card, alignItems:"center" }}>
          <div style={banner}>
            <span style={{ display:"flex" }}><ScanIcon size={15}/></span>
            <div>Show this QR to the <strong style={{ color:G }}>host device</strong>. On the host, click <em>Scan Answer QR</em>.</div>
          </div>
          <div style={{ background:"#fff", padding:14, borderRadius:12 }}>
            <QRCode value={answerUrl || answerCode} size={240} level="L"/>
          </div>
          <p style={{ fontSize:11, color:MUTED, margin:0, textAlign:"center", fontFamily:FONT_MONO, display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
            <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <line x1="12" y1="18" x2="12.01" y2="18"/>
            </svg>
            Scan with iPhone camera to open connection link
          </p>
          <CopyButton text={answerCode} label="Copy Answer Code"/>
          <div style={{ fontSize:10, color:MUTED, textAlign:"center", fontFamily:FONT_MONO, letterSpacing:".06em" }}>
            Waiting for host to complete connection…
          </div>
          <button
            onClick={() => { setStep("scan"); setAnswerCode(""); setAnswerUrl(""); setErrorMsg(""); setPasteInput(""); }}
            style={ghostBtn}
            {...springProps()}>
            Start over
          </button>
        </div>
      )}

      {step === "connected" && rtcDC && (
        <div style={{ ...card, maxWidth:420 }}>
          <div style={{
            ...banner,
            borderColor:"rgba(110,207,110,.3)",
            background:"rgba(110,207,110,.05)",
            color:"rgba(110,207,110,.9)",
          }}>
            <span style={{ display:"flex" }}><CheckCircleIcon size={15}/></span>
            <div>Connected via WebRTC. Mirror tab shows live script + video.</div>
          </div>
          <WebRTCRemotePanel dc={rtcDC} remoteStream={remoteStream}/>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  ShareLinkButton
// ─────────────────────────────────────────────────────────────
function ShareLinkButton({ url }) {
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  if (!canShare) return null;
  const handleShare = async () => {
    try { await navigator.share({ title:"Teleprompter Remote", url }); } catch {}
  };
  return (
    <button
      onClick={handleShare}
      style={{
        flex:1, padding:"11px 0", borderRadius:8, cursor:"pointer",
        background:"transparent", border:`1px solid ${BORDER}`,
        color:TEXT, fontFamily:FONT_MONO, fontSize:11, fontWeight:500,
        letterSpacing:".08em", textTransform:"uppercase",
        display:"flex", alignItems:"center", justifyContent:"center", gap:7,
        transition:T_ALL, WebkitTapHighlightColor:"transparent",
      }}
      onMouseEnter={e=>{ e.currentTarget.style.background=G_ALPHA(.06); e.currentTarget.style.borderColor=BORDER2; }}
      onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor=BORDER; }}
      {...springProps()}>
      <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"/>
        <circle cx="6" cy="12" r="3"/>
        <circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
      Share
    </button>
  );
}


// ─────────────────────────────────────────────────────────────
//  WebRTCModal — host-side pairing modal
// ─────────────────────────────────────────────────────────────
export function WebRTCModal({ open, onClose, onRemoteConnected, roomId, initialOffer }) {
  const [view,         setView]         = useState("menu");
  const [offerCode,    setOfferCode]    = useState("");
  const [offerLink,    setOfferLink]    = useState("");
  const [answerCode,   setAnswerCode]   = useState("");
  const [answerUrl,    setAnswerUrl]    = useState("");
  const [pasteInput,   setPasteInput]   = useState("");
  const [errorMsg,     setErrorMsg]     = useState("");
  const [generating,   setGenerating]   = useState(false);
  const [rtcDC,        setRtcDC]        = useState(null);
  const [showScanner,  setShowScanner]  = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);

  const pcRef          = useRef(null);
  const videoSenderRef = useRef(null);
  const autoStartedRef = useRef(false);

  // Stable replaceTrack function — Provider calls this when bgMode changes
  const replaceVideoTrack = useCallback((track) => {
    if (!videoSenderRef.current) return;
    videoSenderRef.current.replaceTrack(track || null).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && initialOffer && !autoStartedRef.current) {
      autoStartedRef.current = true;
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("offer");
        window.history.replaceState({}, "", u.toString());
      } catch {}
      startRemoteFlow(extractOfferFromInput(initialOffer) || initialOffer);
    }
  }, [open, initialOffer]); // eslint-disable-line

  useEffect(() => {
    if (!open) {
      setView("menu"); setOfferCode(""); setOfferLink("");
      setAnswerCode(""); setAnswerUrl(""); setPasteInput("");
      setErrorMsg(""); setGenerating(false); setShowScanner(false);
      setRemoteStream(null);
    }
  }, [open]);

  // ── Host: generate offer ──────────────────────────────────
  const startHostFlow = async () => {
    setGenerating(true); setErrorMsg("");
    if (pcRef.current) { try { pcRef.current.close(); } catch {} }
    try {
      const pc = createPeerConnection();
      pcRef.current = pc;
      const dc = pc.createDataChannel("tp", { ordered:true });

      // Add video transceiver upfront — replaceTrack() later, no renegotiation needed
      const transceiver = pc.addTransceiver("video", { direction:"sendrecv" });
      videoSenderRef.current = transceiver.sender;

      const handleDCOpen = () => {
        setRtcDC(dc);
        onRemoteConnected(dc, replaceVideoTrack);
        setView("host-connected");
      };
      if (dc.readyState === "open") handleDCOpen();
      else dc.onopen = handleDCOpen;
      dc.onclose = () => { setRtcDC(null); videoSenderRef.current = null; setView("host-disconnected"); };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed") { try { pc.restartIce(); } catch {} }
      };

      const offer = await pc.createOffer({ offerToReceiveAudio:false, offerToReceiveVideo:false });
      await pc.setLocalDescription(offer);
      await waitForICEGathering(pc);

      const sdp     = pc.localDescription.sdp;
      const encoded = encodeForQR(sdp);
      if (!encoded) throw new Error("SDP encoding failed — no valid ICE candidates found.");

      const code     = `o:${encoded}`;
      const shareUrl = (() => {
        try {
          const u = new URL(window.location.href);
          u.searchParams.set("offer", encodeURIComponent(code));
          u.searchParams.set("remote-setup", "1");
          return u.toString();
        } catch { return ""; }
      })();

      setOfferCode(code); setOfferLink(shareUrl);
      setGenerating(false); setView("host-offer");
    } catch (e) {
      setErrorMsg(`Failed to generate offer: ${e.message}`); setGenerating(false);
    }
  };

  // ── Host: accept remote answer ────────────────────────────
  const acceptAnswer = async (raw) => {
    const extracted = extractOfferFromInput(raw);
    let code = extracted;
    try {
      const url = new URL((raw || "").trim());
      const answerParam = url.searchParams.get("answer");
      if (answerParam) code = decodeURIComponent(answerParam);
    } catch {}

    const trimmed = (code || "").trim();
    if (!trimmed) return;
    const pc = pcRef.current;
    if (!pc) { setErrorMsg("No active offer — generate a new one"); return; }
    setErrorMsg(""); setShowScanner(false);
    try {
      let sdp = null;
      if (trimmed.startsWith("v") && trimmed.includes("_")) {
        const decoded = decodeAnswer(trimmed);
        if (decoded) {
          const localSDP = pc.localDescription?.sdp;
          if (!localSDP) { setErrorMsg("No local SDP — regenerate offer"); return; }
          sdp = reconstructAnswerSDP({
            ufrag: decoded.ufrag, pwd: decoded.pwd,
            ip: decoded.ip, port: decoded.port,
            fingerprintHex: decoded.fingerprintHex,
            mids: extractMidsFromSDP(localSDP),
            mlines: extractMlinesFromSDP(localSDP),
          });
        }
      }
      if (!sdp && trimmed.startsWith("a:")) sdp = decodeFromQR(trimmed.slice(2));
      if (!sdp && trimmed.startsWith("v=")) sdp = trimmed;
      if (!sdp) { setErrorMsg("Invalid answer code — could not decode."); return; }
      await pc.setRemoteDescription(new RTCSessionDescription({ type:"answer", sdp }));
    } catch (e) { setErrorMsg(`Connection failed: ${e.message}`); }
  };

  // ── Remote flow inside host modal ─────────────────────────
  const startRemoteFlow = async (raw) => {
    const extracted = extractOfferFromInput(raw);
    if (!extracted) return;
    setGenerating(true); setErrorMsg(""); setShowScanner(false);
    if (pcRef.current) { try { pcRef.current.close(); } catch {} }
    try {
      const sdp = decodeOfferSDP(extracted);
      const pc  = createPeerConnection();
      pcRef.current = pc;

      // Capture host video — eagerly set srcObject on any mounted mirror video
      pc.ontrack = (e) => {
        const stream = e.streams?.[0] || new MediaStream([e.track]);
        const videoEl = document.querySelector(".tp-mirror-video");
        if (videoEl) {
          videoEl.srcObject = stream;
          videoEl.play().catch(() => {});
        }
        setRemoteStream(stream);
      };

      pc.ondatachannel = (e) => {
        const dc = e.channel;
        const handleOpen = () => { setRtcDC(dc); onRemoteConnected(dc); setView("remote-connected"); };
        if (dc.readyState === "open") handleOpen();
        else dc.onopen = handleOpen;
        dc.onclose = () => { setRtcDC(null); setRemoteStream(null); setView("remote-disconnected"); };
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed") { try { pc.restartIce(); } catch {} }
      };

      await pc.setRemoteDescription(new RTCSessionDescription({ type:"offer", sdp }));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForICEGathering(pc);

      const localSDP  = pc.localDescription.sdp;
      const candidate = extractCandidateFromSDP(localSDP);
      const fpHex     = extractFingerprintFromSDP(localSDP);
      const ufrag     = localSDP.match(/a=ice-ufrag:(\S+)/)?.[1] ?? "";
      const pwd       = localSDP.match(/a=ice-pwd:(\S+)/)?.[1]   ?? "";
      if (!candidate) throw new Error("No ICE candidate found.");
      if (!ufrag || !pwd) throw new Error("Missing ICE credentials.");

      const encoded = encodeAnswer(candidate.ip, candidate.port, fpHex, ufrag, pwd);
      if (!encoded) throw new Error("Answer encoding failed.");

      const url = (() => {
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("offer"); u.searchParams.delete("remote-setup");
          u.searchParams.set("answer", encodeURIComponent(encoded));
          return u.toString();
        } catch { return ""; }
      })();

      setAnswerCode(encoded); setAnswerUrl(url);
      setGenerating(false); setView("remote-answer");
    } catch (e) { setErrorMsg(`Remote setup failed: ${e.message}`); setGenerating(false); }
  };

  if (!open) return null;

  // ── Modal style helpers ───────────────────────────────────
  const inputStyle = {
    flex:1, background:"var(--bg)", color:"var(--text)",
    border:`1px solid ${BORDER}`, borderRadius:7,
    padding:"8px 10px", fontFamily:FONT_MONO, fontSize:11,
    outline:"none", transition:"border-color 140ms ease, box-shadow 140ms ease",
  };
  const goBtn   = { padding:"0 16px", background:G, border:"none", borderRadius:7, color:BG, fontFamily:FONT_MONO, fontSize:10, fontWeight:600, cursor:"pointer", transition:T_ALL };
  const backBtn = { fontSize:10, color:MUTED, background:"none", border:"none", cursor:"pointer", fontFamily:FONT_MONO, transition:"color 140ms ease" };
  const fullBtn = (accent) => ({
    width:"100%", padding:accent?"12px 0":"10px 0",
    background:accent?G:CTRL_BG,
    border:accent?"none":`1px solid ${BORDER}`,
    borderRadius:8,
    color:accent?BG:MUTED,
    fontFamily:FONT_MONO, fontSize:accent?12:11,
    fontWeight:accent?600:400,
    letterSpacing:".08em", textTransform:"uppercase",
    cursor:"pointer", transition:T_ALL,
  });
  const scanToggle = (active) => ({
    width:"100%", padding:"9px 0",
    background:active?G_ALPHA(.06):CTRL_BG,
    border:`1px solid ${active?G:BORDER}`,
    borderRadius:8, color:active?G:MUTED,
    fontFamily:FONT_MONO, fontSize:11,
    letterSpacing:".08em", textTransform:"uppercase",
    cursor:"pointer", transition:T_ALL,
  });

  const viewTitles = {
    "menu":"Cross-Device Remote",
    "host-offer":"Step 1 — Share Remote Link",
    "host-scan-answer":"Step 2 — Enter Answer Code",
    "host-connected":"Remote Connected",
    "host-disconnected":"Remote Disconnected",
    "remote-scan-offer":"Scan Host Offer QR",
    "remote-answer":"Show This QR to Host",
    "remote-connected":"Connected to Host",
    "remote-disconnected":"Host Disconnected",
  };

  const mkBanner = (type) => ({
    display:"flex", gap:10, alignItems:"flex-start",
    background: type==="ok"   ? "rgba(102,187,106,.05)"
              : type==="danger"? "rgba(224,85,85,.06)"
              : G_ALPHA(.04),
    border:`1px solid ${
      type==="ok"    ? "rgba(102,187,106,.3)"
      : type==="danger"? "rgba(224,85,85,.3)"
      : G_ALPHA(.15)
    }`,
    borderRadius:10, padding:"12px 16px",
    width:"100%", boxSizing:"border-box",
    fontSize:11, fontFamily:FONT_MONO,
    color: type==="ok"    ? "rgba(102,187,106,.9)"
         : type==="danger"? "rgba(239,83,80,.9)"
         : G_ALPHA(.75),
    letterSpacing:".05em", lineHeight:1.7,
  });

  const inputFocusHandlers = {
    onFocus: e => { e.currentTarget.style.borderColor=G; e.currentTarget.style.boxShadow=`0 0 0 3px ${G_ALPHA(.07)}`; },
    onBlur:  e => { e.currentTarget.style.borderColor=BORDER; e.currentTarget.style.boxShadow="none"; },
  };

  return (
    <div className="tp-modal-overlay open" onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div className="tp-modal" style={{ maxWidth:420, maxHeight:"90dvh", overflowY:"auto" }}>
        <div className="tp-modal-header">
          <span className="tp-modal-title">{viewTitles[view] ?? "Remote"}</span>
          <button className="tp-modal-close" onClick={onClose}><CloseIcon size={16}/></button>
        </div>

        {/* ── Menu ── */}
        {view === "menu" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner()}>
              <span style={{ display:"flex" }}><SignalIcon size={15}/></span>
              <div>
                No server needed — pure WebRTC over your local network.<br/><br/>
                <strong>Host</strong> generates offer QR, <strong>Remote</strong> scans it and shows answer QR, Host scans that back.<br/><br/>
                <strong>Mirror mode</strong> on the remote shows the live script + camera/screen video.
              </div>
            </div>
            <button onClick={startHostFlow} disabled={generating} style={fullBtn(true)} {...springProps()}>
              {generating ? "Generating…" : "I'm the Host — Generate Offer"}
            </button>
            <button onClick={() => setView("remote-scan-offer")} style={fullBtn(false)} {...springProps()}>
              I'm the Remote — Scan Offer
            </button>
            {errorMsg && (
              <div style={{ fontSize:10, color:REC, padding:"6px 10px", background:"rgba(224,85,85,.08)", borderRadius:6, border:"1px solid rgba(224,85,85,.2)", fontFamily:FONT_MONO }}>
                {errorMsg}
              </div>
            )}
          </div>
        )}

        {/* ── Host: show offer QR ── */}
        {view === "host-offer" && (
          <div className="tp-qr-box">
            <div style={mkBanner()}>
              <span style={{ opacity:.7, fontSize:11, fontWeight:700, fontFamily:FONT_MONO }}>01</span>
              <div>Share the link with the remote device, or let them scan the QR.</div>
            </div>
            {offerLink && (
              <div style={{ display:"flex", gap:8, width:"100%", padding:"2px 0" }}>
                <CopyButton text={offerLink} label="Copy Link"/>
                <ShareLinkButton url={offerLink}/>
              </div>
            )}
            <div style={{ fontSize:9, letterSpacing:".1em", color:MUTED, textTransform:"uppercase", fontFamily:FONT_MONO }}>— or scan QR —</div>
            <div style={{ background:"#fff", padding:14, borderRadius:12 }}>
              <QRCode value={offerLink || offerCode} size={220} level="L"/>
            </div>
            <p style={{ fontSize:11, color:MUTED, margin:0, textAlign:"center", fontFamily:FONT_MONO }}>
              📱 Scan with iPhone camera to open connection link
            </p>
            <button onClick={() => setView("host-scan-answer")} style={fullBtn(true)} {...springProps()}>
              Next — Scan Remote's Answer QR
            </button>
          </div>
        )}

        {/* ── Host: scan answer ── */}
        {view === "host-scan-answer" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner()}>
              <span style={{ opacity:.7, fontSize:11, fontWeight:700, fontFamily:FONT_MONO }}>02</span>
              <div>Scan the answer QR on the remote device, or paste the answer code.</div>
            </div>
            <button onClick={() => setShowScanner(s=>!s)} style={scanToggle(showScanner)} {...springProps()}>
              {showScanner ? "Hide Camera" : "Scan Answer QR"}
            </button>
            {showScanner && (
              <JsQRScanner
                label="Scan Remote's Answer QR"
                onScan={(code)=>{ acceptAnswer(code); setShowScanner(false); }}
                onClose={()=>setShowScanner(false)}
              />
            )}
            <div style={{ display:"flex", gap:6 }}>
              <input
                value={pasteInput}
                onChange={e=>setPasteInput(e.target.value)}
                placeholder="Or paste answer code here…"
                style={inputStyle}
                onKeyDown={e=>{ if(e.key==="Enter") acceptAnswer(pasteInput); }}
                {...inputFocusHandlers}
              />
              <button onClick={()=>acceptAnswer(pasteInput)} style={goBtn} {...springProps()}>Connect</button>
            </div>
            {errorMsg && <div style={{ fontSize:10, color:REC, fontFamily:FONT_MONO }}>{errorMsg}</div>}
            <button
              onClick={()=>setView("host-offer")} style={backBtn}
              onMouseEnter={e=>{e.currentTarget.style.color=TEXT;}}
              onMouseLeave={e=>{e.currentTarget.style.color=MUTED;}}>
              ← Back to offer QR
            </button>
          </div>
        )}

        {/* ── Host: connected ── */}
        {view === "host-connected" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner("ok")}>
              <span style={{ display:"flex" }}><CheckCircleIcon size={15}/></span>
              <div>
                WebRTC connected! Remote can see the live script in Mirror mode.
                Turn on Camera or Screen share to send live video to the remote too.
              </div>
            </div>
            <div className="tp-modal-btns">
              <button className="primary" onClick={onClose}>Done — Start Prompting</button>
            </div>
          </div>
        )}

        {/* ── Host: disconnected ── */}
        {view === "host-disconnected" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner("danger")}>
              <span style={{ display:"flex" }}><ZapIcon size={15}/></span>
              <div>The remote device disconnected. Generate a new offer to reconnect.</div>
            </div>
            <div className="tp-modal-btns">
              <button onClick={()=>setView("menu")}>Back</button>
              <button className="primary" onClick={()=>{ setView("menu"); startHostFlow(); }}>Reconnect</button>
            </div>
          </div>
        )}

        {/* ── Remote: scan offer ── */}
        {view === "remote-scan-offer" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner()}>
              <span style={{ display:"flex" }}><ScanIcon size={15}/></span>
              <div>Scan the offer QR on the host device, or paste the offer code below.</div>
            </div>
            <button onClick={()=>setShowScanner(s=>!s)} style={scanToggle(showScanner)} {...springProps()}>
              {showScanner ? "Hide Camera" : "Scan Host Offer QR"}
            </button>
            {showScanner && (
              <JsQRScanner
                label="Scan Host Offer QR"
                onScan={(code)=>{ startRemoteFlow(code); setShowScanner(false); }}
                onClose={()=>setShowScanner(false)}
              />
            )}
            <div style={{ display:"flex", gap:6 }}>
              <input
                value={pasteInput}
                onChange={e=>setPasteInput(e.target.value)}
                placeholder="Or paste offer code here…"
                style={inputStyle}
                onKeyDown={e=>{ if(e.key==="Enter" && !generating) startRemoteFlow(pasteInput); }}
                {...inputFocusHandlers}
              />
              <button onClick={()=>startRemoteFlow(pasteInput)} disabled={generating} style={goBtn} {...springProps()}>
                {generating ? "…" : "Go"}
              </button>
            </div>
            {errorMsg && <div style={{ fontSize:10, color:REC, fontFamily:FONT_MONO }}>{errorMsg}</div>}
            <button
              onClick={()=>setView("menu")} style={backBtn}
              onMouseEnter={e=>{e.currentTarget.style.color=TEXT;}}
              onMouseLeave={e=>{e.currentTarget.style.color=MUTED;}}>
              ← Back
            </button>
          </div>
        )}

        {/* ── Remote: show answer QR ── */}
        {view === "remote-answer" && (
          <div className="tp-qr-box">
            <div style={mkBanner()}>
              <span style={{ opacity:.7, fontSize:11, fontWeight:700, fontFamily:FONT_MONO }}>02</span>
              <div>Show this QR to the <strong>host</strong> device.</div>
            </div>
            <div style={{ background:"#fff", padding:14, borderRadius:12 }}>
              <QRCode value={answerUrl || answerCode} size={220} level="L"/>
            </div>
            <p style={{ fontSize:11, color:MUTED, margin:0, textAlign:"center", fontFamily:FONT_MONO }}>
              📱 Scan with iPhone camera to open connection link
            </p>
            <CopyButton text={answerCode} label="Copy Answer Code"/>
            <div style={{ fontSize:10, color:MUTED, textAlign:"center", fontFamily:FONT_MONO }}>
              Waiting for host to connect…
            </div>
          </div>
        )}

        {/* ── Remote: connected ── */}
        {view === "remote-connected" && rtcDC && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div style={mkBanner("ok")}>
              <span style={{ display:"flex" }}><CheckCircleIcon size={15}/></span>
              <div>Connected via WebRTC. Use Mirror tab for live script + video.</div>
            </div>
            <WebRTCRemotePanel dc={rtcDC} remoteStream={remoteStream}/>
          </div>
        )}

        {/* ── Remote: disconnected ── */}
        {view === "remote-disconnected" && (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div style={mkBanner("danger")}>
              <span style={{ display:"flex" }}><ZapIcon size={15}/></span>
              <div>Lost connection to the host. Try scanning the offer again.</div>
            </div>
            <div className="tp-modal-btns">
              <button onClick={()=>setView("remote-scan-offer")}>Scan Again</button>
              <button className="primary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}